import {BaseGraphNode} from "#graph/nodes/base/node"
import type {NodeParameters} from "#graph/nodes/base/types"
import type {GraphNodeWithRefs, SerializedGraphNode} from "#graph/serialization/types"
import {CircularRef} from "#graph/nodes/circular-ref/node"
import {traverseGraphParameters} from "#graph/helpers/parameters"
import {getRegisteredNode} from "#graph/decorators/register-node"
import {convertVersion} from "#graph/versioning/helpers"

export function deserializeGraphNode(serializedGraph: SerializedGraphNode, disableValidation = false) {
    function deserializeImpl(serializedGraph: SerializedGraphNode, refCache: Map<number, BaseGraphNode>, allowCircularRefs: boolean) {
        const deserializeParameter = (parameter: unknown): unknown => {
            if (parameter instanceof Array) {
                return parameter.map((entry) => deserializeParameter(entry))
            } else if (typeof parameter === "object" && parameter) {
                const {$class, $parameters, $refId, $version} = parameter as NodeParameters
                if (
                    typeof $class === "string" &&
                    typeof $parameters === "object" &&
                    $parameters &&
                    typeof $refId === "number" &&
                    typeof $version === "number"
                ) {
                    if (refCache.has($refId)) return refCache.get($refId)

                    if ($class === "GraphRef") {
                        if (!allowCircularRefs) {
                            throw new Error(`Cached ref ${$refId} not found`)
                        }
                        return new CircularRef({refId: $refId})
                    }

                    return deserializeImpl(
                        {
                            $class,
                            $parameters: $parameters as NodeParameters,
                            $refId,
                            $version,
                        },
                        refCache,
                        true,
                    )
                } else
                    return Object.entries(parameter).reduce<NodeParameters>((acc, [key, value]) => {
                        acc[key] = deserializeParameter(value)
                        return acc
                    }, {})
            } else return parameter
        }

        const {$class, $parameters, $refId, $version} = serializedGraph

        const NodeClass = getRegisteredNode($class)
        const parameters = convertVersion(deserializeParameter($parameters) as NodeParameters, $version, NodeClass.versionChain ?? [], NodeClass.nodeClass)
        const retVal = new NodeClass(parameters, true)

        //Graphs are cached recursively, do not move this outside of this function
        refCache.set($refId, retVal)

        //Replace circular references with the actual graph instances as soon as the come available
        for (const graph of refCache.values()) {
            let resolvedRef = false
            const newParameters = traverseGraphParameters(
                graph.parameters,
                (value) => value instanceof BaseGraphNode,
                (node: BaseGraphNode) => {
                    if (node instanceof CircularRef) {
                        const refGraph = refCache.get(node.parameters.refId)
                        if (!refGraph || refGraph instanceof CircularRef) return node
                        resolvedRef = true
                        return refGraph
                    }
                    return undefined
                },
            )
            if (resolvedRef) {
                graph.replaceParameters(newParameters, true)
            }
        }

        return retVal
    }

    /*collects all serialized graphs and tracks the subgraphs referenced by them*/
    const collectReferencedGraphs = (serializedGraph: SerializedGraphNode): Map<number, GraphNodeWithRefs> => {
        const nodeGraphsWithRef = new Map<number, GraphNodeWithRefs>()
        const collectReferences = (parameter: unknown, parentIds: number[]) => {
            if (parameter instanceof Array) {
                //mixed array of primitives and graphs
                parameter.forEach((entry, index) => collectReferences(entry, parentIds))
            } else if (typeof parameter === "object" && parameter) {
                const {$class, $parameters, $refId, $version} = parameter as NodeParameters

                if (
                    typeof $class === "string" &&
                    typeof $parameters === "object" &&
                    $parameters &&
                    typeof $refId === "number" &&
                    typeof $version === "number"
                ) {
                    if ($class === "GraphRef") {
                        //Reference to another graph
                        parentIds.forEach((parentRefId, index) => {
                            const parent = nodeGraphsWithRef.get(parentRefId)
                            if (!parent) throw new Error(`Parent refId ${parentRefId} not found`)
                            parent.referencedGraphs.add($refId)
                        })
                    } else {
                        //Real graph instance
                        if (nodeGraphsWithRef.has($refId)) throw new Error(`Duplicate refId ${$refId}`)
                        nodeGraphsWithRef.set($refId, {refId: $refId, nodeGraph: parameter as SerializedGraphNode, referencedGraphs: new Set<number>()})
                        collectReferences($parameters, [...parentIds, $refId])
                    }
                } else {
                    //some object with various keys: Parameter object or something else
                    Object.entries(parameter).forEach(([key, value]) => {
                        collectReferences(value, parentIds)
                    })
                }
            }
        }

        if (serializedGraph.$class === "GraphRef") throw new Error("GraphRef not allowed in this context")

        collectReferences(serializedGraph, [])
        return nodeGraphsWithRef
    }

    const nodeGraphsWithRef = collectReferencedGraphs(serializedGraph)
    const refCache = new Map<number, BaseGraphNode>()

    /*The loop deserializes all graphs in a specific order, such that referenced subgraphs are always in the cache.
        This is required, because our DB stores the graphs in jsonb format which has random order of keys. I.e. the keys
        are potentially returned in different order as originally serialized.
        The different scheduling always works if there are no cycles in the graph.
        If there are cycles in the graph, we resolve them one by one by temporarily assigning a special CircularRefNode that gets resolved later in the process*/
    while (nodeGraphsWithRef.size > 0) {
        let deserializeableGraphs = [...nodeGraphsWithRef.values()].filter((value) => {
            return Array.from(value.referencedGraphs).every((refId) => refCache.has(refId))
        })

        let allowCircularRefs = false
        if (deserializeableGraphs.length === 0) {
            //No new graphs can be deserialized, because of cyclic dependencies
            //We just pick the first unresolvable, and allow circular references for this one iteration
            allowCircularRefs = true
            deserializeableGraphs = [[...nodeGraphsWithRef.values()][0]]
        }

        for (const {refId, nodeGraph} of deserializeableGraphs) {
            if (refCache.has(refId)) continue //the graph was deserialized in a prev iteration, while deserializing a parent.

            const existingGraphIds = new Set(refCache.keys())
            deserializeImpl(nodeGraph, refCache, allowCircularRefs)
            const newlyDeserializedGraphIds = Array.from(new Set(refCache.keys())).filter((key) => !existingGraphIds.has(key))

            if (newlyDeserializedGraphIds.length === 0) throw new Error("Deserialization failed, no new graphs deserialized")

            newlyDeserializedGraphIds.forEach((newlyDeserializedGraphId) => {
                nodeGraphsWithRef.delete(newlyDeserializedGraphId)
                nodeGraphsWithRef.forEach((value) => value.referencedGraphs.delete(newlyDeserializedGraphId))
            })
        }
    }

    //Sanity check to see if all circular refs are resolved and apply automatic zod schema transformations
    for (const [key, graph] of refCache.entries()) {
        traverseGraphParameters(
            graph.parameters,
            (value) => value instanceof BaseGraphNode,
            (node) => {
                if (node instanceof CircularRef) {
                    throw new Error(`Circular ref ${node.parameters.refId} not resolved`)
                }
            },
        )
    }

    const nodeGraph = refCache.get(serializedGraph.$refId)
    if (!nodeGraph) {
        throw new Error(`Root node graph ${serializedGraph.$refId} not found`)
    }

    return nodeGraph
}
